import React, { useState, useEffect, useRef } from "react";
import { auth } from "../../config/firebase";
import { onAuthStateChanged, signInWithCustomToken } from "firebase/auth";
import ChatService from "../../services/FirebaseChatService";
import MainButton from "../../components/Elements/MainButton/MainButton";
import MainInput from "../../components/Elements/MainInput/MainInput";
import PageHeader from "../../components/Elements/PageHeader/PageHeader";
import "./SupportChats.css";

const SupportChats = () => {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState("");

  const messagesEndRef = useRef(null);

  // Auto-scroll na dół listy wiadomości
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  }, [messages, selectedChat?.id]);

  // 1. Zjednoczona autoryzacja Firebase
  useEffect(() => {
    const loginWithBackendToken = async () => {
      const token = localStorage.getItem("firebaseToken");
      if (token && !auth.currentUser) {
        try {
          const cred = await signInWithCustomToken(auth, token);
          await cred.user.getIdTokenResult(true);
        } catch (error) {
          console.error("Firebase custom token login failed:", error);
        }
      }
    };

    loginWithBackendToken();

    const unsub = onAuthStateChanged(auth, (user) => {
      setFirebaseUser(user);
    });

    return unsub;
  }, []);

  // 2. Subskrypcja wszystkich czatów
  useEffect(() => {
    if (!firebaseUser) {
      setChats([]);
      return;
    }
    const unsub = ChatService.subscribeToAllChats(setChats);
    return unsub;
  }, [firebaseUser]);

  // 3. Subskrypcja wiadomości wybranego czatu z czyszczeniem poprzedniego stanu
  useEffect(() => {
    if (!selectedChat?.id || !firebaseUser) {
      setMessages([]);
      return;
    }

    setMessages([]); // Czyszczenie starych wiadomości przed załadowaniem nowych
    const unsub = ChatService.subscribeToChatMessages(selectedChat.id, setMessages);
    return unsub;
  }, [selectedChat?.id, firebaseUser]);

  // Auto-selekcja pierwszego czatu
  useEffect(() => {
    if (chats.length > 0 && !selectedChat) {
      setSelectedChat(chats[0]);
    }
  }, [chats, selectedChat]);

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedChat?.id || !firebaseUser) return;

    await ChatService.sendMessage(selectedChat.id, replyText.trim(), firebaseUser.email, "support");
    setReplyText("");
  };

  const groupMessagesByDate = (messagesList) => {
    if (!messagesList.length) return [];

    const groups = [];
    let currentGroup = { date: messagesList[0].createdAt?.toDate(), messages: [messagesList[0]], isNewDay: true };

    for (let i = 1; i < messagesList.length; i++) {
      const msgDate = messagesList[i].createdAt?.toDate();
      const prevDate = currentGroup.date;

      const isNew = !isSameDay(msgDate, prevDate);

      if (isNew) {
        groups.push(currentGroup);
        currentGroup = { date: msgDate, messages: [messagesList[i]], isNewDay: true };
      } else {
        currentGroup.messages.push(messagesList[i]);
      }
    }
    groups.push(currentGroup);
    return groups;
  };

  const formatChatListDate = (timestamp) => {
    if (!timestamp?.toDate) return "Never";

    const date = timestamp.toDate();
    const now = new Date();

    if (isSameDay(date, now)) return "Today";

    const yesterday = new Date();
    yesterday.setDate(now.getDate() - 1);

    if (isSameDay(date, yesterday)) return "Yesterday";

    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  const formatDate = (date) => {
    if (!date) return "Today";
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (isSameDay(date, today)) return "Today";
    if (isSameDay(date, yesterday)) return "Yesterday";

    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <main>
      <PageHeader
        title="Support Chats"
        description="View chat and message back users, to help them with their issues."
      />
      <div className="admin-help-chats">
        <div className="unified-chat-container">
          <div className="chats-split-layout">
            {/* LEFT: Chats List */}
            <div className="chats-list-panel">
              <div className="panel-header">
                <h2>Chats</h2>
              </div>
              <div className="chats-list">
                {chats.map((chat) => (
                  <div
                    key={chat.id}
                    className={`chat-item ${selectedChat?.id === chat.id ? "active" : ""}`}
                    onClick={() => setSelectedChat(chat)}
                  >
                    <div className="chat-user">{chat.email || chat.userId || "Unknown"}</div>
                    <div className="chat-preview">{chat.lastMessage}</div>{" "}
                    <div className="chat-time">{formatChatListDate(chat.lastMessageAt)}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="vertical-divider" />

            {/* RIGHT: Chat Detail */}
            <div className="chat-detail-panel">
              {selectedChat ? (
                <>
                  <div className="panel-header">
                    <h3>Chat with {selectedChat.email || selectedChat.userId || "User"}</h3>
                  </div>

                  <div className="messages-container">
                    {groupMessagesByDate(messages).map((group, idx) => (
                      <div key={group.date?.toISOString() || `group-${idx}`} className="message-group">
                        {group.isNewDay && <div className="date-divider">{formatDate(group.date)}</div>}
                        {group.messages.map((msg) => (
                          <div
                            key={msg.id}
                            className={`message ${msg.sender === "support" ? "admin-message" : "user-message"}`}
                          >
                            <div className="message-bubble">
                              <div className="message-text">{msg.text}</div>
                              <div className="message-time">
                                {msg.createdAt?.toDate()
                                  ? msg.createdAt.toDate().toLocaleTimeString("pl-PL", {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      hour12: false,
                                    })
                                  : ""}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                  <div className="reply-section">
                    <MainInput
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Type your reply..."
                      className="reply-input"
                    />
                    <MainButton onClick={handleSendReply} disabled={!replyText.trim()} className="send-button">
                      Send Reply
                    </MainButton>
                  </div>
                </>
              ) : (
                <div className="no-chat-selected">
                  {chats.length === 0 ? <h3>Loading chats...</h3> : <h3>Select a conversation</h3>}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default SupportChats;
